// COLLABORATION BRANDS · 협업 브랜드 — BI sponsor_brand distinct 리스트(전량).
//
// 현정 소유(BI) 브랜드 리스트만. 딜/매출 상세는 혜원 Campaigns·Financial 영역 → 링크 안내만.
// row: 브랜드명(ellipsis) + N건 · 최근협업월(YYYY.MM). 빈 배열이면 "협업 브랜드 없음".
// 전량 반환이라 리스트는 패널 내부 스크롤(CONFIRMED/RECENT와 동일 패턴, 헤더·안내는 스크롤 밖).
import { Link as LinkIcon } from 'lucide-react'
import type { ProfileData, ChannelSubs } from '../../creatorsApi'
import { formatYearMonth } from '../overview/format'
import { SectionHeader } from './SectionHeader'
import { PlatformTabs } from '../PlatformTabs'
import { AiJudgedNotice } from '../AiJudgedNotice'
import { channelEmptyText } from '../platformDefault'
import type { Platform } from '../../creatorsApi'

function metaOf(item: ProfileData['collaborationBrands'][number]): string {
  const parts: string[] = []
  if (item.contentCount != null && Number.isFinite(item.contentCount))
    parts.push(`${item.contentCount}건`)
  if (item.lastAt?.trim()) parts.push(formatYearMonth(item.lastAt))
  return parts.join(' · ')
}

export function CollaborationBrands({
  brands,
  platform,
  onPlatformChange,
  busy,
  channels,
}: {
  brands: ProfileData['collaborationBrands']
  /** 탭을 붙이려면 둘 다 준다. 없으면 탭 없이 렌더(기존 호출부 호환). */
  platform?: Platform
  onPlatformChange?: (platform: Platform) => void
  /** 새 플랫폼 응답 대기 중 — 탭 옆에 표시한다(아래 값은 아직 이전 플랫폼 것이다). */
  busy?: boolean
  /** 사유 판정 입력 — 계정 없음 / 수집 대상 아님 / 브랜디드 없음을 가른다. */
  channels?: ChannelSubs[]
}) {
  return (
    <div className="min-w-0 p-[26px_28px]">
      <SectionHeader
        label="COLLABORATION BRANDS · 협업 브랜드"
        info={platform && onPlatformChange ? (
          <PlatformTabs value={platform} onChange={onPlatformChange} label="협업 브랜드 플랫폼" busy={busy} />
        ) : undefined}
      />
      <AiJudgedNotice className="mb-4" />

      {brands.length === 0 ? (
        <div className="border border-dashed border-line-soft bg-surface-soft p-5 font-text text-[14px] text-fg-3">
          {channelEmptyText(platform ?? 'youtube', channels)}
        </div>
      ) : (
        // 활동 이력 패널과 정합되는 최대 높이 + 내부 스크롤(전량 표시).
        <div className="max-h-[370px] overflow-y-auto">
          {brands.map((b, i) => {
            const meta = metaOf(b)
            return (
              <div
                key={`${b.brand}-${i}`}
                className={[
                  'flex items-baseline justify-between gap-3 py-[11px]',
                  i < brands.length - 1 ? 'border-b border-line-hairline' : '',
                ].join(' ')}
              >
                <span className="min-w-0 truncate font-display text-[16px] font-medium text-ink-800">
                  {b.brand}
                </span>
                {meta && (
                  <span className="flex-none whitespace-nowrap font-text text-[12px] tabular-nums text-fg-3">
                    {meta}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}


      {/* 혜원 경계: 딜/매출은 Campaigns·Financial 탭 */}
      <div className="mt-4 flex items-start gap-2 border-t border-line-hairline pt-4">
        <LinkIcon className="mt-px h-[15px] w-[15px] flex-none text-brand-blue" aria-hidden />
        <span className="break-keep font-text text-[12px] leading-[1.55] text-fg-2">
          협찬/브랜디드 콘텐츠 기준 브랜드 리스트입니다. 딜·매출 상세는{' '}
          <span className="font-medium text-brand-blue">Campaigns · Financial 탭</span>에서
          확인하세요.
        </span>
      </div>
    </div>
  )
}
